/* UTILS */
import { useMutation, useQueryClient } from '@tanstack/vue-query';

import { deleteBatch, retryBatch } from '@/api/calls/batches/batches.api';
import Reporter from '@/api/clients/reporter/reporter';
import { displayGenericErrorToaster, displayGenericSuccessToaster } from '@/api/helpers/helpers';
import { t } from '@/config/i18n';
import type { Batch } from '@/types/batches/batches.types';

/* MUTATIONS */
// DELETE
export function useDeleteBatchMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batch: Batch) => deleteBatch(batch.uuid),
    onSuccess: (_res, ctx) => {
      queryClient.invalidateQueries({ queryKey: ['getBatches'] });
      queryClient.invalidateQueries({ queryKey: ['getBatchesSummary'] });
      displayGenericSuccessToaster(t('recruiter_network.queue.toaster.remove', { count: ctx.nb_total_operations }));
      // REPORTER
      let batchType = 'in_progress';
      if (ctx.finished_at) {
        if (ctx.nb_failed_operations) {
          batchType = ctx.nb_failed_operations < ctx.nb_total_operations ? 'partially_failed' : 'failed';
        } else {
          batchType = 'success';
        }
      }
      Reporter().reportData('batch_deleted', {
        batch_type: batchType
      });
    },
    onError: () => displayGenericErrorToaster()
  });
}

// RETRY
export function useRetryBatchMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batch: Batch) => retryBatch(batch.uuid),
    onSuccess: (_res, ctx) => {
      queryClient.invalidateQueries({ queryKey: ['getBatches'] });
      queryClient.invalidateQueries({ queryKey: ['getBatchesSummary'] });
      displayGenericSuccessToaster(t('recruiter_network.queue.toaster.success', { success: ctx.nb_failed_operations }));

      // REPORTER
      Reporter().reportData('batch_relaunched', {
        batch_type: ctx.nb_failed_operations < ctx.nb_total_operations ? 'partially_failed' : 'failed'
      });
    },
    onError: () => displayGenericErrorToaster()
  });
}
